import { KeyboardEvent, MutableRefObject, RefObject, useEffect, useRef } from 'react';
import styled from 'styled-components';

import {
  DOWN_ARROW,
  ENTER_KEY,
  LEFT_ARROW,
  RIGHT_ARROW,
  SPACE_KEY,
  UP_ARROW,
} from 'constants/keyCodes';
import { body1, colors, spacing, subtitle2 } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import noop from 'utils/noop';

import { DRAG_DROP_OPTIONS } from '../../modals/constants';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Description = styled.span`
  ${body1}
  text-align: left;
`;

const OptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const OptionRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${spacing.plusHalf};

  gap: ${spacing.plusHalf};
`;

const OptionWrapper = styled.div<{ isSelected: boolean }>`
  display: inline-flex;
  flex: 1 1 0;
  flex-direction: column;
  justify-content: center;

  align-content: space-around;
  border-radius: ${spacing.half};
  background: ${colors.grey5};
  border: ${pxToRem(3)} solid ${colors.white};

  :focus {
    outline: none;
    ${({ isSelected }) =>
      !isSelected &&
      `
      border: ${pxToRem(1.5)} solid ${colors.grey50};
      margin: ${pxToRem(1.5)};
  `}
  }

  ${({ isSelected }) =>
    isSelected &&
    `
    border: ${pxToRem(3)} solid ${colors.purple50};
  `}
`;

const OptionText = styled.span`
  ${subtitle2}
  margin: ${spacing.threeQuarter} 0;
  padding: 0;
  text-align: center;
`;

const SvgWrapper = styled.div`
  display: inline-flex;
  justify-content: center;
  margin-top: ${spacing.base};
`;

const handleKeyPress = (
  { keyCode }: KeyboardEvent<HTMLDivElement>,
  refs: MutableRefObject<HTMLDivElement | undefined>[],
) => {
  try {
    const focusedIndex = refs.findIndex((opt) => opt.current === document.activeElement);
    const isItemFocused = focusedIndex > -1;
    const focusedItem = refs[focusedIndex].current;

    switch (keyCode) {
      case UP_ARROW: {
        if (isItemFocused && focusedIndex < 3) {
          refs[focusedIndex + 6].current?.focus();
        } else if (isItemFocused) {
          refs[focusedIndex - 3].current?.focus();
        }
        break;
      }

      case DOWN_ARROW: {
        if (isItemFocused && focusedIndex > 5) {
          refs[focusedIndex - 6]?.current?.focus();
        } else if (isItemFocused) {
          refs[focusedIndex + 3].current?.focus();
        }
        break;
      }

      case RIGHT_ARROW: {
        if (isItemFocused && focusedIndex % 3 === 2) {
          refs[focusedIndex - 2].current?.focus();
        } else if (isItemFocused) {
          refs[focusedIndex + 1].current?.focus();
        }
        break;
      }

      case LEFT_ARROW: {
        if (isItemFocused && focusedIndex % 3 === 0) {
          refs[focusedIndex + 2].current?.focus();
        } else if (isItemFocused) {
          refs[focusedIndex - 1].current?.focus();
        }
        break;
      }

      case SPACE_KEY:
      case ENTER_KEY: {
        focusedItem?.click();
        break;
      }

      default: {
        return;
      }
    }
  } catch (err) {
    console.error(err); // eslint-disable-line
  }
};

type DragDropSelectorProps = {
  /* content strings to render */
  content: any;
  /* Function to select an DragDrop */
  setSelectedDragDrop: (arg: string) => void;
  /* String of the option selected */
  selectedDragDrop: string;
};

/**
 * setSelectedDragDrop
 * @param { any= } setSelectedDragDrop setter for the selected drag drop
 * @param { string } selectedDragDrop  selected drag drop identifier
 * @param { any } content object containing content
 */
export const DragDropSelector = ({
  setSelectedDragDrop = noop,
  selectedDragDrop,
  content,
  ...props
}: DragDropSelectorProps) => {
  const { description } = content;
  const optionIds = Object.keys(DRAG_DROP_OPTIONS);
  const refs = optionIds.map(() => useRef<HTMLDivElement>());

  useEffect(() => refs[0].current?.focus(), []);

  return (
    <Wrapper data-test="drag-and-drop-template-selector" {...props}>
      <Description>{description}</Description>
      <OptionContainer onKeyDown={(e) => handleKeyPress(e, refs)}>
        {[0, 1, 2].map((num) => (
          <OptionRow key={`row-${num + 1}`}>
            {[0, 1, 2].map((num2) => {
              const idx = num * 3 + num2;
              const { id, svg, label } =
                DRAG_DROP_OPTIONS?.[optionIds?.[idx] as keyof typeof DRAG_DROP_OPTIONS];
              return (
                <OptionWrapper
                  data-test={`${optionIds[idx]}-option`}
                  tabIndex={0}
                  key={`row-${num + 1}-item-${num2 + 1}`}
                  ref={refs[idx] as RefObject<HTMLDivElement>}
                  onClick={() => setSelectedDragDrop(id)}
                  isSelected={selectedDragDrop === id}
                >
                  <SvgWrapper>{svg({})}</SvgWrapper>
                  <OptionText>{label}</OptionText>
                </OptionWrapper>
              );
            })}
          </OptionRow>
        ))}
      </OptionContainer>
    </Wrapper>
  );
};
